import { useEffect, useState } from 'react';
import { api, type Bench, type Brand, type Card, type CreditBalance, type Idea, type ModelInfo } from '../../lib/api';
import { creditEvents } from '../../lib/creditEvents';
import { QUICK_TABS, TAB_LABEL, type Hand, type QuickTab } from '../../lib/quick';
import { QuickImage } from './QuickImage';
import { QuickEdit } from './QuickEdit';
import { QuickMusic } from './QuickMusic';
import { QuickVideo } from './QuickVideo';
import { QuickMovie } from './QuickMovie';
import { QuickLibrary } from './QuickLibrary';

// ---------------------------------------------------------------------------
// The Quick Studio: one view, six tabs, the price on every button.
//
// The full studio is seventeen benches and a vocabulary. This is the second
// door onto the same rooms: describe the picture, press, see it. Everything
// a tab makes files exactly where the Pro bench would file it — the same
// assets table, the same cards, the same cast — so nothing is lost when a
// creator walks through to Pro, and "Open in Pro" carries the form across.
//
// The shell reads the catalogue for the four spending benches and the
// balance once and hands them down (204); a tab never asks for them itself,
// so four tabs cannot show four different lists. The balance is re-read
// whenever any press announces a spend. What a tab hands to another (a
// picture to Edit, a clip to Movie) goes through `pass`, read once.
// ---------------------------------------------------------------------------

/** One asset handed from one tab to another, read once by the tab that receives it. */
export interface Pass { tab: QuickTab; assetId: string }

export type Catalogue = Partial<Record<Bench, ModelInfo[]>>;

export interface TabProps {
  brand: Brand | null;
  /** The catalogue per bench; a bench missing is still loading. */
  models: Catalogue;
  credits: CreditBalance | null;
  /** An asset another tab handed over; cleared by the tab once read. */
  pass: Pass | null;
  onUsedPass: () => void;
  /** Hand an asset to another tab and switch to it. */
  onPass: (p: Pass) => void;
  onOpenInPro: Hand;
  onGo: (bench: string, move: string | null, prefill?: Idea | null) => void;
  /** A proposal the Idea Lab handed over in Quick mode, read once. */
  prefill?: Idea | null;
  onUsedPrefill?: () => void;
  cards: Card[];
  onCardMade: (c: Card) => void;
  onError: (m: string | null) => void;
}

const SPENDING: Bench[] = ['image', 'edit', 'music', 'video'];

export function Quick({ brand, tab, onTab, onOpenInPro, onGo, prefill, onUsedPrefill, cards, onCardMade }: {
  brand: Brand | null; tab: QuickTab; onTab: (t: QuickTab) => void;
  onOpenInPro: Hand; onGo: TabProps['onGo'];
  prefill?: Idea | null; onUsedPrefill?: () => void;
  cards: Card[]; onCardMade: (c: Card) => void;
}) {
  const [models, setModels] = useState<Catalogue>({});
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [pass, setPass] = useState<Pass | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all(SPENDING.map((b) => api.models(b).then((list) => [b, list] as const)))
      .then((pairs) => { if (live) { setModels(Object.fromEntries(pairs)); setPriceError(null); } })
      .catch((e) => { if (live) setPriceError(`The model list did not load — ${(e as Error).message}. Nothing can be bought until it does.`); });
    const readBalance = () => { api.credits().then((c) => { if (live) setCredits(c); }).catch(() => undefined); };
    readBalance();
    creditEvents.addEventListener('spent', readBalance);
    creditEvents.addEventListener('short', readBalance);
    return () => { live = false; creditEvents.removeEventListener('spent', readBalance); creditEvents.removeEventListener('short', readBalance); };
  }, []);

  // Switching tabs clears the last error: it belonged to the other bench.
  useEffect(() => { setError(null); }, [tab]);

  const props: TabProps = {
    brand, models, credits, pass, onUsedPass: () => setPass(null),
    onPass: (p) => { setPass(p); onTab(p.tab); },
    onOpenInPro, onGo, prefill, onUsedPrefill, cards, onCardMade, onError: setError,
  };

  return (
    <section className="quick" data-tool="quick-studio">
      <div className="quick-head">
        <h2>{TAB_LABEL[tab]}</h2>
        <div className="quick-tabs" role="tablist" aria-label="Quick studio">
          {QUICK_TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn tiny${tab === t ? ' pri' : ''}`} onClick={() => onTab(t)}>{TAB_LABEL[t]}</button>
          ))}
        </div>
      </div>
      {priceError && <p className="err" role="alert">{priceError}</p>}
      {error && <p className="err" role="alert">{error}</p>}
      <div className="quick-bench" data-tool={`quick-${tab}`}>
        {tab === 'image' ? <QuickImage {...props} />
          : tab === 'edit' ? <QuickEdit {...props} />
          : tab === 'music' ? <QuickMusic {...props} />
          : tab === 'video' ? <QuickVideo {...props} />
          : tab === 'movie' ? <QuickMovie {...props} />
          : tab === 'library' ? <QuickLibrary {...props} />
          : null}
      </div>
    </section>
  );
}
